const {registerBlockType} = wp.blocks;
const {RichText} = wp.blockEditor;
const {Button} = wp.components;
const {useEffect} = wp.element;

import { tableAccordion } from '../../../resources/scripts/module/accordion';

registerBlockType('mailmetrics/features-list', {
  title: 'Features List',
  icon: 'list-view',
  category: 'layout',
  supports: {
    align: true,
    html: false,
  },
  attributes: {
    headerColumns: {
      type: 'array',
      default: ['Features', 'Business', 'Enterprise'],
    },
    features: {
      type: 'array',
      default: [],
    },
    accordions: {
      type: 'array',
      default: [],
    }
  },
  edit: ({attributes, setAttributes}) => {
    const { headerColumns, features, accordions } = attributes;

    useEffect(() => {
      tableAccordion();
    }, [accordions.length]);

    const addFeatureRow = () => {
      const newRow = {feature: '', business: '', enterprise: ''};
      setAttributes({features: [...features, newRow]});
    };

    const updateFeatureRow = (index, column, value) => {
      const updatedFeatures = features.map((row, i) => {
        if (i === index) {
          return {...row, [column]: value};
        }
        return row;
      });
      setAttributes({features: updatedFeatures});
    };

    const removeFeatureRow = (index) => {
      const updatedFeatures = features.filter((_, i) => i !== index);
      setAttributes({features: updatedFeatures});
    };

    const toggleCheck = (index, column) => {
      const updatedFeatures = features.map((row, i) => {
        if (i === index) {
          return {...row, [column]: !row[column]};
        }
        return row;
      });
      setAttributes({ features: updatedFeatures });
    };

    const addAccordionSection = () => {
      const newAccordion = {
        header: null,
        rows: [{ content: '', business: false, enterprise: false, businessCheck: false, enterpriseCheck: false }]
      };
      setAttributes({ accordions: [...accordions, newAccordion] });
    };

    // Remove an accordion section
    const removeAccordionSection = (accordionIndex) => {
      const newAccordions = accordions.filter((_, index) => index !== accordionIndex);
      setAttributes({ accordions: newAccordions });
    };

    // Add a new row to an accordion section
    const addAccordionRow = (accordionIndex) => {
      const newRows = [...accordions[accordionIndex].rows, { content: '', business: false, enterprise: false }];
      const newAccordions = [...accordions];
      newAccordions[accordionIndex].rows = newRows;
      setAttributes({ accordions: newAccordions });
    };

    // Remove a row from an accordion section
    const removeAccordionRow = (accordionIndex, rowIndex) => {
      const newRows = accordions[accordionIndex].rows.filter((_, index) => index !== rowIndex);
      const newAccordions = [...accordions];
      newAccordions[accordionIndex].rows = newRows;
      setAttributes({ accordions: newAccordions });
    };

    const updateAccordionRow = (accordionIndex, rowIndex, column, value) => {
      const newAccordions = [...attributes.accordions];
      newAccordions[accordionIndex].rows[rowIndex][column] = value;
      setAttributes({ accordions: newAccordions });
    };

    const renderAccordions = () => {
      const toggleCheck = (accordionIndex, rowIndex, column) => {
        const newAccordions = [...accordions];
        newAccordions[accordionIndex].rows[rowIndex][column] = !newAccordions[accordionIndex].rows[rowIndex][column];
        setAttributes({ accordions: newAccordions });
      };

      const handleCheckToggle = (accordionIndex, rowIndex, column, event) => {
        event.stopPropagation(); // Stop event from bubbling up
        const newAccordions = [...accordions];
        newAccordions[accordionIndex].rows[rowIndex][column] = !newAccordions[accordionIndex].rows[rowIndex][column];
        setAttributes({ accordions: newAccordions });
      };

      return accordions.map((accordion, accordionIndex) => (
        <div className="table-accordion" key={accordionIndex}>
          <RichText
            tagName="div"
            className="table-accordion-header accordion-header"
            value={accordion.header}
            onChange={(value) => {
              const newAccordions = [...accordions];
              newAccordions[accordionIndex].header = value;
              setAttributes({ accordions: newAccordions });
            }}
            placeholder={'New Section'}
          />
          <div className="table-accordion-content">
            <table>
              {accordion.rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  <td>
                    <RichText
                      value={row.content}
                      onChange={(value) => updateAccordionRow(accordionIndex, rowIndex, 'content', value)}
                      placeholder={'Text'}
                    />
                  </td>
                  <td>
                    {row.businessCheck ? <i className="check"></i> :
                      <RichText
                        value={row.business}
                        onChange={(value) => updateAccordionRow(accordionIndex, rowIndex, 'business', value)}
                        placeholder={'Text'}
                      />}
                    <Button
                      isSmall
                      onClick={(event) => handleCheckToggle(accordionIndex, rowIndex, 'businessCheck', event)}
                    >
                      {row.businessCheck ? 'Remove Check' : 'Add Check'}
                    </Button>
                  </td>
                  <td>
                    {row.enterpriseCheck ? <i className="check"></i> :
                      <RichText
                        value={row.enterprise}
                        onChange={(value, event) => updateAccordionRow(accordionIndex, rowIndex, 'enterprise', value)}
                        placeholder={'Text'}
                      />}
                    <Button
                      isSmall
                      onClick={(event) => handleCheckToggle(accordionIndex, rowIndex, 'enterpriseCheck', event)}
                    >
                      {row.enterpriseCheck ? 'Remove Check' : 'Add Check'}
                    </Button>
                  </td>
                  <td>
                    <Button isDestructive onClick={() => removeAccordionRow(accordionIndex, rowIndex)}>
                      Remove Row
                    </Button>
                  </td>
                </tr>
              ))}
            </table>
            <Button isSmall onClick={() => addAccordionRow(accordionIndex)}>
              Add Row
            </Button>
          </div>
          <Button isDestructive onClick={() => removeAccordionSection(accordionIndex)}>
            Remove Accordion
          </Button>
        </div>
      ));
    };

    return (
      <section className="features-list">
        <div className="inner">
          <div className={'table-wrapper'}>
            <div className="table-header">
              {headerColumns.map((text, index) => (
                <RichText
                  key={index}
                  tagName="div"
                  className="table-header-column"
                  value={text}
                  placeholder={'Feature'}
                  onChange={(value) => {
                    const newHeaderColumns = [...headerColumns];
                    newHeaderColumns[index] = value;
                    setAttributes({headerColumns: newHeaderColumns});
                  }}
                />
              ))}
            </div>
            <div className="table-content">
              <table>
                <tbody key={'tbody'}>
                {features.map((row, index) => (
                  <tr key={index}>
                    <td>
                      <RichText
                        value={row.feature}
                        onChange={(value) => updateFeatureRow(index, 'feature', value)}
                        placeholder={'Text'}
                      />
                    </td>
                    <td>
                      {row.businessCheck
                        ? <i className="check"></i>
                        : <RichText
                            value={row.business}
                            onChange={(value) => updateFeatureRow(index, 'business', value)}
                            placeholder={'Text'}
                          />
                      }
                      <Button
                        isSmall
                        onClick={() => toggleCheck(index, 'businessCheck')}
                      >
                        {row.businessCheck ? 'Remove Check' : 'Add Check'}
                      </Button>
                    </td>
                    <td>
                      {row.enterpriseCheck
                        ? <i className="check"></i>
                        : <RichText
                          value={row.enterprise}
                          onChange={(value) => updateFeatureRow(index, 'enterprise', value)}
                          placeholder={'Text'}
                        />
                      }
                      <Button
                        isSmall
                        onClick={() => toggleCheck(index, 'enterpriseCheck')}
                      >
                        {row.enterpriseCheck ? 'Remove Check' : 'Add Check'}
                      </Button>
                    </td>
                    <td>
                      <Button isDestructive onClick={() => removeFeatureRow(index)}>
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))}
                </tbody>
              </table>
              <Button isPrimary onClick={addFeatureRow}>
                Add Feature Row
              </Button>

              {renderAccordions()}
              <br/>
              <Button isPrimary onClick={addAccordionSection}>
                Add Accordion Section
              </Button>
            </div>
          </div>
        </div>
      </section>
    );
  },
  save: ({attributes}) => {
    const {headerColumns, features, accordions } = attributes;

    const renderSavedAccordions = () => {
      return attributes.accordions.map((accordion, accordionIndex) => (
        <div className="table-accordion" key={accordionIndex}>
          <div className="table-accordion-header accordion-header">
            {accordion.header}
          </div>
          <div className="table-accordion-content">
            <table>
              {accordion.rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  <td>{row.content ? row.content : '-'}</td>
                  <td>{row.businessCheck ? <i className="check"></i> : row.business ? row.business : '-'}</td>
                  <td>{row.enterpriseCheck ? <i className="check"></i> : row.enterprise ? row.enterprise : '-'}</td>
                </tr>
              ))}
            </table>
          </div>
        </div>
      ));
    };

    return (
      <section className="features-list">
        <div className="inner">
          <div className="table-wrapper">
            <div className="table-header">
              {headerColumns.map((text, index) => (
                <div key={index} className="table-header-column">{text ?? '-'}</div>
              ))}
            </div>
            <div className="table-content">
              <table>
                {features.map((row, index) => (
                  <tr key={index}>
                    <td>{row.feature ? row.feature : '-'}</td>
                    <td>
                      {row.businessCheck ? <i className="check"></i> : row.business ? row.business : '-'}
                    </td>
                    <td>
                      {row.enterpriseCheck ? <i className="check"></i> : row.enterprise ? row.enterprise : '-'}
                    </td>
                  </tr>
                ))}
              </table>
              {renderSavedAccordions()}
            </div>
          </div>
        </div>
      </section>
    );
  },
});
